import { Button } from '@liza/ui/components/ui/button';
import { PanelBottomOpenIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';
import { useEncounterDrawer } from '../EncounterDrawerContext';
import { Allergy } from '../RecordDetails/Allergy';
import { Condition } from '../RecordDetails/Condition';
import { Document } from '../RecordDetails/Document';
import { Encounter } from '../RecordDetails/Encounter';
import { FamilyMember } from '../RecordDetails/FamilyMember';
import { Immunization } from '../RecordDetails/Immunization';
import { LabOrder } from '../RecordDetails/LabOrder';
import { Medication } from '../RecordDetails/Medication';
import { Procedure } from '../RecordDetails/Procedure';
import { ServiceRequest } from '../RecordDetails/ServiceRequest';
import { LabDetailContent } from './LabDetailContent';
import { SurveyDetailContent } from './SurveyDetailContent';
import { VitalDetailContent } from './VitalDetailContent';
import { hasDataSelection } from './hasDataSelection';
import type { DataResource } from './types';

type Props = {
  resource: DataResource;
  selectedId?: string;
  labIdentifier?: string;
  vitalIdentifier?: string;
  surveyIdentifier?: string;
  medicationResourceType?: 'MedicationRequest' | 'MedicationStatement';
  medplumPatientId: string;
  onNavigateToResource: (opts: { resource: DataResource; id?: string; labIdentifier?: string }) => void;
};

export const DetailColumn = ({
  resource,
  selectedId,
  labIdentifier,
  vitalIdentifier,
  surveyIdentifier,
  medicationResourceType,
  medplumPatientId,
  onNavigateToResource,
}: Props): React.ReactNode => {
  const { t } = useTranslation('common');
  const { t: tRecords } = useTranslation('records');
  const { openEncounter, openLabOrder } = useEncounterDrawer();

  if (!hasDataSelection({ resource, selectedId, labIdentifier, vitalIdentifier, surveyIdentifier })) {
    return (
      <div className="flex h-full items-center justify-center bg-muted/50 p-8 text-sm text-muted-foreground">
        {t('selectRecordToViewDetails')}
      </div>
    );
  }

  const content = match(resource)
    .with('careTeam', () => null)
    .with('conditions', () => <Condition id={selectedId!} />)
    .with('documents', () => <Document id={selectedId!} />)
    .with('medications', () => (
      <Medication
        id={selectedId!}
        resourceType={medicationResourceType ?? 'MedicationRequest'}
        medplumPatientId={medplumPatientId}
      />
    ))
    .with('allergies', () => <Allergy id={selectedId!} />)
    .with('immunizations', () => <Immunization id={selectedId!} />)
    .with('encounters', () => <Encounter id={selectedId!} medplumPatientId={medplumPatientId} />)
    .with('procedures', () => (
      <Procedure
        id={selectedId!}
        onSelectResultObservation={(analyteId) => onNavigateToResource({ resource: 'labs', labIdentifier: analyteId })}
        onSelectEncounter={(encounterId) => onNavigateToResource({ resource: 'encounters', id: encounterId })}
        onOpenEncounterInDrawer={(encounterId) => openEncounter({ id: encounterId, title: tRecords('note') })}
      />
    ))
    .with('familyHistory', () => <FamilyMember id={selectedId!} />)
    .with('socialHistory', () => null)
    .with('surveys', () => (
      <SurveyDetailContent surveyIdentifier={surveyIdentifier!} medplumPatientId={medplumPatientId} />
    ))
    .with('serviceRequests', () => <ServiceRequest id={selectedId!} />)
    .with('labOrders', () => (
      <LabOrder
        id={selectedId!}
        medplumPatientId={medplumPatientId}
        onSelectLab={(labId) => onNavigateToResource({ resource: 'labs', labIdentifier: labId })}
      />
    ))
    .with('labs', () => (
      <LabDetailContent
        labIdentifier={labIdentifier!}
        medplumPatientId={medplumPatientId}
        onSelectLabOrder={(id) => onNavigateToResource({ resource: 'labOrders', id })}
      />
    ))
    .with('vitals', () => <VitalDetailContent vitalIdentifier={vitalIdentifier!} medplumPatientId={medplumPatientId} />)
    .exhaustive();

  // Encounters and lab orders are the two record types the shared drawer has a view for, so they get an
  // affordance to open there as well. The Data tab keeps its own selection — the drawer params are
  // separate from `resource`/`id` precisely so the two surfaces can show different records at once.
  const openInDrawer = (): void => {
    if (resource === 'encounters') openEncounter({ id: selectedId!, title: tRecords('note') });
    if (resource === 'labOrders') openLabOrder({ id: selectedId!, title: tRecords('labOrders') });
  };

  if (resource !== 'encounters' && resource !== 'labOrders') return content;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 justify-end border-b px-3 py-1.5">
        <Button variant="ghost" size="sm" onClick={openInDrawer} data-testid="open-in-drawer-button">
          <PanelBottomOpenIcon className="size-4" />
          {t('openInDrawer')}
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">{content}</div>
    </div>
  );
};
